const DOP = {
  60: 70,
  80: 90,
  100: 110,
  120: 135,
  140: 155,
  160: 180,
  180: 200,
  200: 220,
  220: 245,
  240: 265,
  260: 290,
  280: 310,
  300: 330,
  320: 355,
  340: 375,
  360: 400,
  380: 420,
  400: 440
};
const parseConfig = root => {
  const raw = root.getAttribute("data-calculator");

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

const formatNumber = (num, dec, locale) =>
  num.toLocaleString(locale || document.documentElement.lang || "en", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec
  });
const toSnakeCase = key =>
  key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
const replacePlaceholders = (template, values) => {
  let result = String(template || "");

  Object.keys(values).forEach(key => {
    const value = String(values[key]);
    const snakeKey = toSnakeCase(key);

    result = result.split(`{${key}}`).join(value);
    result = result.split(`{${snakeKey}}`).join(value);
  });

  return result;
};

const setHtml = (root, name, html) => {
  const el = root.querySelector(`[data-calc-out="${name}"]`);

  if (el) {
    el.innerHTML = html;
  }
};

const setText = (root, name, text) => {
  const el = root.querySelector(`[data-calc-out="${name}"]`);

  if (el) {
    el.textContent = text;
  }
};

const setBadgeState = (root, state) => {
  const badge = root.querySelector("[data-calc-badge]");

  if (!badge) {
    return;
  }

  badge.classList.remove("is-ok", "is-warn", "is-bad");
  badge.classList.add(`is-${state}`);
};

const inputEl = (root, name) =>
  root.querySelector(`[data-calc-input="${name}"]`);
const withUnit = (value, unit) => `${value} <small>${unit}</small>`;
const RESULT_DEBOUNCE_MS = 1500;
const PARAM_VALUE_MAX = 100;
/* eslint-disable camelcase */
const trimParam = value => {
  const text = String(value || "").trim();

  if (text.length <= PARAM_VALUE_MAX) {
    return text;
  }

  return text.slice(0, PARAM_VALUE_MAX);
};

const roundMetric = (num, decimals) => {
  const factor = 10 ** decimals;

  return Math.round(num * factor) / factor;
};

const getCalculatorContext = config => {
  const context = {};

  if (config && typeof config.type === "string" && config.type) {
    context.calculator_type = config.type;
  }

  if (config && config.productId != null) {
    context.product_id = Number(config.productId) || 0;
  }

  if (
    config &&
    typeof config.productTitle === "string" &&
    config.productTitle
  ) {
    context.product_title = trimParam(config.productTitle);
  }

  if (config && typeof config.locale === "string" && config.locale) {
    context.locale = config.locale;
  }

  return context;
};

const pushCalculatorEvent = (config, eventName, payload) => {
  if (typeof eventName !== "string" || !eventName) {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  const data = Object.assign(
    {},
    getCalculatorContext(config),
    payload && typeof payload === "object" ? payload : {},
    { event: eventName }
  );

  window.dataLayer.push(data);
};

const glassLwSnapshot = (status, uValue, thickness, area, bags, pallets) => ({
  reportable: true,
  payload: {
    result_status: status,
    u_value: uValue,
    thickness_mm: thickness,
    area_m2: area,
    bags,
    pallets
  }
});
const uValueSnapshot = (
  status,
  uValue,
  rafterThickness,
  rafterSpacing,
  insulationBetween,
  insulationUnder,
  woolBetween,
  woolUnder
) => ({
  reportable: true,
  payload: {
    result_status: status,
    u_value: uValue,
    rafter_thickness_mm: rafterThickness,
    rafter_spacing_mm: rafterSpacing,
    insulation_between_mm: insulationBetween,
    insulation_under_mm: insulationUnder,
    wool_between_lambda: woolBetween,
    wool_under_lambda: woolUnder
  }
});
const emptyUValueSnapshot = () => ({
  reportable: false,
  payload: {
    result_status: "empty"
  }
});
const buildCtaPayload = (config, snapshot, cta) => {
  const source = snapshot && snapshot.payload ? snapshot.payload : {};
  const payload = {
    result_status: source.result_status || "empty",
    cta_url: trimParam(cta.getAttribute("href") || ""),
    cta_label: trimParam(cta.textContent || "")
  };

  if (typeof source.u_value === "number") {
    payload.u_value = source.u_value;
  }

  if (config && config.type === "glass-lw" && typeof source.bags === "number") {
    payload.bags = source.bags;
  }

  return payload;
};

const viewThresholds = root => {
  const thresholds = [0.5];
  const blockHeight = root.getBoundingClientRect().height;
  const viewportHeight = window.innerHeight || 0;

  if (blockHeight <= 0 || viewportHeight <= 0) {
    return thresholds;
  }

  const halfViewportRatio = (viewportHeight * 0.5) / blockHeight;

  if (halfViewportRatio > 0 && halfViewportRatio < 0.5) {
    thresholds.unshift(Math.max(0.01, Number(halfViewportRatio.toFixed(2))));
  }

  return thresholds;
};

const isCalculatorSeen = entry => {
  if (entry.intersectionRatio >= 0.5) {
    return true;
  }

  const viewportHeight = entry.rootBounds
    ? entry.rootBounds.height
    : window.innerHeight;

  return entry.intersectionRect.height >= viewportHeight * 0.5;
};

const observeCalculatorView = (root, config) => {
  if (typeof window.IntersectionObserver !== "function") {
    pushCalculatorEvent(config, "product_calculator_view");

    return;
  }

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting || !isCalculatorSeen(entry)) {
          return;
        }

        observer.disconnect();
        pushCalculatorEvent(config, "product_calculator_view");
      });
    },
    { threshold: viewThresholds(root) }
  );

  observer.observe(root);
};

const createCalculatorTracker = (root, config) => {
  let started = false;
  let lastFingerprint = "";
  let resultTimer = 0;
  let snapshot = null;
  const publishSnapshot = next => {
    snapshot = next;
  };

  const pushResult = () => {
    if (!snapshot || !snapshot.reportable || !snapshot.payload) {
      return;
    }

    const fingerprint = JSON.stringify(snapshot.payload);

    if (fingerprint === lastFingerprint) {
      return;
    }

    lastFingerprint = fingerprint;
    pushCalculatorEvent(config, "product_calculator_result", snapshot.payload);
  };

  const scheduleResult = () => {
    window.clearTimeout(resultTimer);
    resultTimer = window.setTimeout(pushResult, RESULT_DEBOUNCE_MS);
  };

  const onUserInput = event => {
    const input =
      event.target && event.target.closest
        ? event.target.closest("[data-calc-input]")
        : null;

    if (!input || !root.contains(input)) {
      return;
    }

    if (!started) {
      started = true;
      pushCalculatorEvent(config, "product_calculator_start", {
        field: input.getAttribute("data-calc-input") || ""
      });
    }

    scheduleResult();
  };

  const cta = root.querySelector(".c-product-calculator__cta-btn");

  root.addEventListener("input", onUserInput);
  root.addEventListener("change", onUserInput);

  if (cta) {
    cta.addEventListener("click", () => {
      pushCalculatorEvent(
        config,
        "product_calculator_cta",
        buildCtaPayload(config, snapshot, cta)
      );
    });
  }

  observeCalculatorView(root, config);

  return publishSnapshot;
};
/* eslint-enable camelcase */

const initGlassLw = (root, config) => {
  const thicknessSelect = inputEl(root, "thickness");
  const areaInput = inputEl(root, "area");
  const locale = config.locale;
  const units = config.units || {};
  const messages = config.messages || {};
  const umax = Number(config.umax) || 0.15;
  const lambda = Number(config.lambda) || 0.04;
  const density = Number(config.density) || 30;
  const bagWeight = Number(config.bagWeight) || 15;
  const bagsPerPallet = Number(config.bagsPerPallet) || 30;
  const defaultThickness = Number(config.defaultThickness) || 280;

  if (!thicknessSelect || !areaInput) {
    return;
  }

  const publishSnapshot = createCalculatorTracker(root, config);

  Object.keys(DOP)
    .map(Number)
    .forEach(d => {
      const opt = document.createElement("option");

      opt.value = String(d);
      opt.textContent = String(d);

      if (d === defaultThickness) {
        opt.selected = true;
      }

      thicknessSelect.appendChild(opt);
    });

  const recalc = () => {
    const d = Number(thicknessSelect.value);
    const area = Math.max(0, Number(areaInput.value) || 0);
    const dmin = DOP[d];
    const r = d / (lambda * 1000);
    const u = 1 / r;
    const formattedUmax = formatNumber(umax, 2, locale);
    const formattedU = formatNumber(u, 3, locale);
    const consumptionPerM2 = (dmin / 1000) * density;
    const totalKg = consumptionPerM2 * area;
    const bagsTotal = totalKg / bagWeight;
    const pallets = bagsTotal / bagsPerPallet;
    const volume = totalKg / density;
    const bagsCeil = Math.ceil(bagsTotal);
    const palletsCeil = Math.ceil(bagsTotal / bagsPerPallet);

    setHtml(root, "dmin", withUnit(dmin, units.mm || "mm"));
    setHtml(root, "r", withUnit(formatNumber(r, 2, locale), units.r || ""));
    setHtml(root, "u", withUnit(formattedU, units.u || ""));
    setHtml(
      root,
      "bags",
      withUnit(formatNumber(bagsTotal, 1, locale), units.bags || "")
    );
    setText(root, "pallets", formatNumber(pallets, 1, locale));
    setHtml(
      root,
      "volume",
      withUnit(formatNumber(volume, 1, locale), units.volume || "")
    );
    setText(
      root,
      "palletsLabel",
      replacePlaceholders(messages.pallets, {
        bagsPerPallet
      })
    );
    setHtml(
      root,
      "orderNote",
      replacePlaceholders(messages.orderNote, {
        bagWeight,
        bags: `<b>${bagsCeil}</b>`,
        pallets: `<b>${palletsCeil}</b>`
      })
    );
    setText(
      root,
      "disclaimer",
      replacePlaceholders(messages.disclaimer, {
        lambda,
        density,
        bagWeight
      })
    );

    const resultStatus = u <= umax ? "ok" : "warn";

    if (resultStatus === "ok") {
      setBadgeState(root, "ok");
      setText(
        root,
        "badge",
        replacePlaceholders(messages.badgeOk, {
          umax: formattedUmax,
          u: formattedU
        })
      );
    } else {
      setBadgeState(root, "warn");
      setText(
        root,
        "badge",
        replacePlaceholders(messages.badgeWarn, {
          umax: formattedUmax,
          u: formattedU
        })
      );
    }

    publishSnapshot(
      glassLwSnapshot(
        resultStatus,
        roundMetric(u, 3),
        d,
        area,
        bagsCeil,
        palletsCeil
      )
    );
  };

  thicknessSelect.addEventListener("change", recalc);
  areaInput.addEventListener("input", recalc);
  recalc();
};

const initUValue = (root, config) => {
  const d5 = inputEl(root, "d5");
  const e5 = inputEl(root, "e5");
  const b7 = inputEl(root, "b7");
  const b12 = inputEl(root, "b12");
  const o5 = inputEl(root, "o5");
  const o6 = inputEl(root, "o6");
  const locale = config.locale;
  const units = config.units || {};
  const messages = config.messages || {};
  const umax = Number(config.umax) || 0.15;
  const lambdaRafter = Number(config.lambdaRafter) || 0.23;
  const rsi = Number(config.rsi) || 0.1;
  const rse = Number(config.rse) || 0.1;

  if (!d5 || !e5 || !b7 || !b12 || !o5 || !o6) {
    return;
  }

  const publishSnapshot = createCalculatorTracker(root, config);
  const recalc = () => {
    const rafterThickness = Number(d5.value);
    const rafterSpacing = Number(e5.value);
    const insulationBetween = Number(b7.value);
    const insulationUnder = Number(b12.value);
    const woolBetween = Number(o5.value);
    const woolUnder = Number(o6.value);
    const formattedUmax = formatNumber(umax, 2, locale);
    const hasInputs =
      rafterThickness > 0 && rafterSpacing > 0 && insulationBetween > 0;

    setText(
      root,
      "reqNote",
      replacePlaceholders(messages.reqNote, { umax: formattedUmax })
    );
    setText(
      root,
      "disclaimer",
      replacePlaceholders(messages.disclaimer, {
        lambdaRafter,
        rsi,
        rse
      })
    );

    if (!hasInputs) {
      setHtml(root, "u", withUnit("—", units.u || ""));
      setBadgeState(root, "ok");
      setText(root, "badge", messages.badgeEmpty || "");
      publishSnapshot(emptyUValueSnapshot());

      return;
    }

    const totalThickness = insulationBetween + insulationUnder;
    const areaA = rafterThickness * totalThickness * 0.000001;
    const areaB = rafterSpacing * totalThickness * 0.000001;
    const fractionA = areaA / (areaA + areaB);
    const fractionB = areaB / (areaA + areaB);
    const rtA =
      rsi +
      insulationBetween / 1000 / lambdaRafter +
      insulationUnder / 1000 / woolUnder +
      rse;
    const rtB =
      rsi +
      insulationBetween / 1000 / woolBetween +
      insulationUnder / 1000 / woolUnder +
      rse;
    const rrPrime = 1 / (fractionA / rtA + fractionB / rtB);
    const lambdaMixed = fractionA * lambdaRafter + fractionB * woolBetween;
    const rrBis =
      rsi +
      insulationUnder / 1000 / woolUnder +
      insulationBetween / 1000 / lambdaMixed +
      rse;
    const rr = (rrPrime + rrBis) / 2;
    const u = 1 / rr;
    const formattedU = formatNumber(u, 3, locale);

    setHtml(root, "u", withUnit(formattedU, units.u || ""));

    const resultStatus = u <= umax ? "ok" : "bad";

    if (resultStatus === "ok") {
      setBadgeState(root, "ok");
      setText(
        root,
        "badge",
        replacePlaceholders(messages.badgeOk, {
          u: formattedU,
          umax: formattedUmax
        })
      );
    } else {
      setBadgeState(root, "bad");
      setText(
        root,
        "badge",
        replacePlaceholders(messages.badgeBad, {
          u: formattedU,
          umax: formattedUmax
        })
      );
    }

    publishSnapshot(
      uValueSnapshot(
        resultStatus,
        roundMetric(u, 3),
        rafterThickness,
        rafterSpacing,
        insulationBetween,
        insulationUnder,
        roundMetric(woolBetween, 3),
        roundMetric(woolUnder, 3)
      )
    );
  };

  [d5, e5, b7, b12, o5, o6].forEach(el => {
    el.addEventListener("input", recalc);
    el.addEventListener("change", recalc);
  });
  recalc();
};

(() => {
  const roots = document.querySelectorAll("[data-calculator]");

  roots.forEach(root => {
    const config = parseConfig(root);

    if (!config) {
      return;
    }

    if (config.type === "glass-lw") {
      initGlassLw(root, config);
    } else if (config.type === "u-value") {
      initUValue(root, config);
    }
  });
})();
